import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  createMeetingId,
  storeEvent,
  getExistDeal,
  getSingleEventList,
  updateEvent,
  joinMeetingForGuest,
} from '@/api/event.api';
import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import { successHandler, errorHandler } from '@/utils/toastHandler';

import { Box, TextField } from '@mui/material';
import { Button } from '@/components/UI/Button';
import { FormSelect } from '@/components/UI/Select';
import { Flex } from '@/layout/Flex';
import { convertToSelectData } from '@/utils/convert';
import { FormWrapper, InputGroupWrapper, CopyIconWarpper } from './style';
import PageContainer from '@/components/Container/PageContainer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Loading from '@/components/UI/Loading';
import { dmpMeetingFailure } from '@/utils/dmpMeeting';
import useCurrentLang from '@/hooks/useCurrentLang';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';

const NewEvent = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const { inputRegister } = useAuthValidation();
  const [meetingURL, setMeetingURL] = useState(' ');
  const navigate = useNavigate();
  const lang = useCurrentLang();
  const { userData } = useAppSelector(getAuthSelector);
  //   const [noticeAry, setNoticeAry] = useState<Notice[]>([]);
  const [dealData, setDealData] = useState<DealDetail[]>();
  const [existOption, setExistOption] = useState<Array<string | number>>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [vcId, setVcId] = useState<string>('');

  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dealId, setDealId] = useState<string | null>(null);
  const [guestId, setGuestId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [showSelect, setShowSelect] = useState<boolean>(true);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const inputProps = {
    step: 15,
    min: 30,
  };

  const { handleSubmit, control, setValue, watch, formState: { isSubmitting } } = useForm<EventFormFields>();

  const initData = useCallback(async () => {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5);
    const pad = (value: number) => String(value).padStart(2, '0');
    const currentDate = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    const currentTime = pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':00';

    setValue('evt_startDate', currentDate);
    setValue('evt_startTime', currentTime);
    setValue('evt_endTime', '30');

    try {
      const res = (await getExistDeal()).data;
      if (res.success) {
        setDealData(res.data);
        let defaultOption = '';
        const option: string[] = [];
        res.data.forEach((item: ExistDeal) => {
          option.push(item.name);
          if (searchParams.get('deal') && item.id.toString() === searchParams.get('deal')) {
            defaultOption = item.name;
          }
        });

        setExistOption(option);
        if (searchParams.get('deal')) {
          setValue('evt_type', defaultOption);
          setShowSelect(false);
        }
        setIsLoading(false);
      }
    } catch (error) {}

    // 檢查是否有deal傳入
    if (searchParams.get('deal')) {
      setDealId(searchParams.get('deal'));
      setGuestId(searchParams.get('id'));
    }
  }, [location.search]);

  const getEvent = async () => {
    if (!searchParams.get('edit')) return;
    setEditId(searchParams.get('edit'));
    try {
      const res = (await getSingleEventList(Number(searchParams.get('edit')))).data;
      if (res.success) {
        setValue('evt_name', res.data.evt_name);
        setValue('evt_note', res.data.evt_note);
        setVcId(typeof res.data.vc?.meetingID === 'string' ? res.data.vc.meetingID : '');
        setDealId(res.data.evt_dl_id ? String(res.data.evt_dl_id) : res.data.deal?.id ? String(res.data.deal.id) : null);
        setGuestId(res.data.evt_guest_id ? String(res.data.evt_guest_id) : null);
        const start = res.data.evt_startDate?.split(' ');
        if (start && start.length >= 2) {
          setValue('evt_startDate', start[0]);
          setValue('evt_startTime', start[1]);
          const endsAt = new Date(String(res.data.evt_endDate || '').replace(' ', 'T')).getTime();
          const startsAt = new Date(String(res.data.evt_startDate).replace(' ', 'T')).getTime();
          if (Number.isFinite(endsAt) && endsAt > startsAt) setValue('evt_endTime', String((endsAt - startsAt) / 60000));
        }
        setIsEdit(true);
      }
    } catch (error) {}
  };

  useEffect(() => {
    initData();
    getEvent();
  }, [initData]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const startTime = data.evt_startTime.length === 5 ? data.evt_startTime + ':00' : data.evt_startTime;
      const endTime = new Date(data.evt_startDate + 'T' + startTime);
      const minutes = Number(data.evt_endTime);
      if (!Number.isFinite(endTime.getTime()) || !Number.isFinite(minutes) || minutes <= 0) {
        errorHandler(t('error.server'));
        return;
      }
      endTime.setMinutes(endTime.getMinutes() + minutes);
      const pad = (value: number) => String(value).padStart(2, '0');
      const endTimeString = endTime.getFullYear() + '-' + pad(endTime.getMonth() + 1) + '-' + pad(endTime.getDate())
        + ' ' + pad(endTime.getHours()) + ':' + pad(endTime.getMinutes()) + ':00';
      const eventData = {
        evt_name: data.evt_type ? data.evt_type + ': ' + data.evt_name : data.evt_name,
        evt_note: data.evt_note || '',
        evt_startDate: data.evt_startDate + ' ' + startTime,
        evt_endDate: endTimeString,
        evt_dl_id: dealId || '',
        evt_guest_id: guestId || '',
      };
      let res;
      if (editId) {
        // Editing changes metadata only. Missing connections use the explicit host repair action.
        res = (await updateEvent(Number(editId), eventData)).data;
      } else {
        // A failed meeting request must never become a successful event with an empty vc_id.
        const meetingId = vcId || await createMeetingId();
        setVcId(meetingId);
        res = (await storeEvent({ ...data, ...eventData, vc_id: meetingId, evt_host_id: userData.id })).data;
      }
      if (!res.success) throw { response: { data: res } };
      successHandler(t(editId ? 'success.update' : 'success.upload'));
      navigate('/event');
    } catch (error) {
      errorHandler(dmpMeetingFailure(error, lang === 'en'));
    }
  });

  // 監視evt_type的變化, 若有變化則更新dealId和guestId
  // 要找出和userId不同的id
  useEffect(() => {
    const subscription = watch((values) => {
      if (!dealData || !values.evt_type) return;
      const deal = dealData.find((item) => item.name === values.evt_type);
      if (!deal) return;
      setDealId(String(deal.id));
      const participant = deal.participants.find((item) => Number(item.acc_id) !== Number(userData.id));
      setGuestId(participant ? String(participant.acc_id) : null);
    });
    return () => subscription.unsubscribe();
  }, [watch, dealData, userData.id]);

  const copyMeetingLink = async () => {
    // 若meetingURL為空則不執行
    if (meetingURL === ' ') return;

    // 針對safari瀏覽器的複製功能
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(meetingURL);
        successHandler(t('success.copied'));
      } catch (error) {}
      return;
    }

    // 針對其他瀏覽器的複製功能
    const textArea = document.createElement('textarea');
    textArea.value = meetingURL;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
    successHandler(t('success.copied'));
  };

  return (
    <PageContainer title={t('pages.event.newEvent.title')}>
      {isLoading ? (
        <Loading />
      ) : (
        <FormWrapper onSubmit={onSubmit}>
          <Flex flexDirection={'column'} gridGap={'45px'}>
            {!showSelect && !isEdit && (
              <Controller
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div>
                    <TextField
                      type="text"
                      variant="standard"
                      label={t('form.existingDeal.label')}
                      value={value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={!!error}
                      helperText={error ? error.message : null}
                      inputProps={{ readOnly: true }}
                      autoComplete="off"
                    />
                  </div>
                )}
                name={'evt_type'}
              />
            )}

            {showSelect && !isEdit && (
              <FormSelect
                control={control}
                data={convertToSelectData(existOption)}
                inputLabel={t('form.existingDeal.label')}
                selectLabel={t('form.existingDeal.label')}
                name={'evt_type'}
              />
            )}

            <Controller
              control={control}
              name={'evt_name'}
              defaultValue={''}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  variant="standard"
                  label={t('form.addATitle.label')}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                  autoComplete="off"
                />
              )}
              rules={inputRegister}
            />

            <InputGroupWrapper>
              <Controller
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div>
                    <TextField
                      type="date"
                      variant="standard"
                      label={lang === 'en' ? 'Meeting date' : '會議日期'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      autoComplete="off"
                    />
                  </div>
                )}
                name={'evt_startDate'}
                rules={inputRegister}
              />
              <Controller
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div>
                    <TextField
                      type="time"
                      variant="standard"
                      label={lang === 'en' ? 'Start time' : '開始時間'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      autoComplete="off"
                    />
                  </div>
                )}
                name={'evt_startTime'}
                rules={inputRegister}
              />
              <Controller
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Flex alignItems={'flex-end'}>
                    <TextField
                      type="number"
                      variant="standard"
                      label={lang === 'en' ? 'Duration (minutes)' : '預計時長（分鐘）'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      inputProps={inputProps}
                      autoComplete="off"
                    />
                    {t('pages.event.newEvent.minutes')}
                  </Flex>
                )}
                name={'evt_endTime'}
                rules={inputRegister}
              />
              {/* {meetingURL === ' ' && (
                <Button
                  onClick={URLGenerator}
                  style={{ borderRadius: 5, marginLeft: 'auto', padding: 0 }}
                  size={'lg'}
                  variant={'primary'}
                  type="button"
                >
                  {t('buttons.generateURL')}
                </Button>
              )}
              {meetingURL !== ' ' && (
                <Button
                  style={{ borderRadius: 5, marginLeft: 'auto', padding: 0 }}
                  size={'lg'}
                  variant={'normal'}
                  type="button"
                >
                  {t('buttons.generateURL')}
                </Button>
              )} */}
            </InputGroupWrapper>

            {/* <Controller
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div style={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    disabled
                    variant="standard"
                    label={t('form.meetingURL.label')}
                    value={meetingURL}
                    error={!!error}
                    helperText={error ? error.message : null}
                    autoComplete="off"
                  />
                  {meetingURL !== ' ' && (
                    <CopyIconWarpper>
                      <Button type="button" onClick={copyMeetingLink}>
                        <img src="/images/event/copy.svg" alt="" />
                      </Button>
                    </CopyIconWarpper>
                  )}
                </div>
              )}
              name={'vc_id'}
            /> */}

            <Controller
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <div>{t('pages.event.newEvent.addADescription')}</div>
                  <TextField
                    fullWidth
                    inputProps={{ 'aria-label': t('pages.event.newEvent.addADescription') }}
                    variant="standard"
                    label={''}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    autoComplete="off"
                  />
                </>
              )}
              name={'evt_note'}
            />
            <Box className="event-form-actions">
              <Button type="submit" size={'lg'} variant={'primary'} disabled={isSubmitting}>
                {t('buttons.save')}
              </Button>
              <Button type={'button'} onClick={() => navigate('/event')} size={'lg'}>
                {t('buttons.back')}
              </Button>
            </Box>
          </Flex>
        </FormWrapper>
      )}
    </PageContainer>
  );
};

export default NewEvent;
